"use client";

import React from "react";
import { MapPin, Phone, Clock, ArrowRight, Navigation, ExternalLink } from "lucide-react";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const INFO = [
  {
    icon: MapPin,
    label: "Địa chỉ",
    value: "73 Đường ĐHT 31, Phường Tân Hưng Thuận, Quận 12, TPHCM",
    sub: null,
    href: "https://maps.google.com/?q=73+ĐHT+31+Tân+Hưng+Thuận+Quận+12+TPHCM",
  },
  {
    icon: Phone,
    label: "Hotline",
    value: "0325 805 893",
    sub: "Gọi ngay để được tư vấn",
    href: "tel:0325805893",
  },
  {
    icon: Clock,
    label: "Giờ mở cửa",
    value: "18:00 – 22:00",
    sub: "Thứ 2 – Chủ nhật",
    href: null,
  },
];

const NEARBY = ["Gò Vấp", "Hóc Môn", "Bình Dương", "Thủ Đức", "Bình Thạnh"];

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const LocationSection: React.FC = () => {
  return (
    <section
      aria-labelledby="location-heading"
      itemScope
      itemType="https://schema.org/LocalBusiness"
      style={{
        backgroundColor: "var(--section-bg)",
        color: "var(--section-foreground)",
        fontFamily: "var(--font-roboto)",
      }}
      className="w-full py-20 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Hidden schema fields */}
      <span itemProp="name" className="hidden">Guitar Sài Gòn</span>
      <span itemProp="telephone" className="hidden">0325805893</span>
      <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress" className="hidden">
        <span itemProp="streetAddress">73 Đường ĐHT 31</span>
        <span itemProp="addressLocality">Quận 12</span>
        <span itemProp="addressRegion">TP. Hồ Chí Minh</span>
        <span itemProp="addressCountry">VN</span>
      </span>

      <div className="max-w-7xl mx-auto">

        {/* ── HEADER ── */}
        <header className="text-center mb-16">

          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-6 border"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.35)",
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            <MapPin className="w-4 h-4" />
            Cửa hàng Guitar – Quận 12
          </div>

          {/* H2 */}
          <h2
            id="location-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-5 tracking-tight"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--section-foreground)" }}
          >
            Guitar Sài Gòn –{" "}
            <span
              className="relative inline-block"
              style={{ color: "var(--brand-primary)" }}
            >
              Quận 12
              <span
                className="absolute left-0 -bottom-1 w-full h-0.5 rounded-full"
                style={{ backgroundColor: "var(--brand-primary)", opacity: 0.4 }}
              />
            </span>
            , TPHCM
          </h2>

          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: "var(--section-muted)" }}
          >
            Nếu bạn đang tìm nơi{" "}
            <strong style={{ color: "var(--section-foreground)", fontWeight: 600 }}>
              mua guitar ở Quận 12
            </strong>{" "}
            hoặc{" "}
            <strong style={{ color: "var(--section-foreground)", fontWeight: 600 }}>
              mua guitar ở TPHCM
            </strong>
            , Guitar Sài Gòn là địa điểm thuận tiện cho khách tại Sài Gòn và các khu vực lân cận.
          </p>

          {/* Divider */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.3)" }} />
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.3)" }} />
          </div>
        </header>

        {/* ── MAIN CARD ── */}
        <div
          className="rounded-3xl overflow-hidden border"
          style={{
            backgroundColor: "var(--section-card)",
            borderColor: "var(--section-border)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.07), 0 0 0 1px rgba(200,169,110,0.08)",
          }}
        >
          <div className="lg:grid lg:grid-cols-[1fr_420px]">

            {/* ── MAP PANEL ── */}
            <div className="relative h-72 lg:h-full min-h-[460px] overflow-hidden">
              {/* Gold top bar */}
              <div
                className="absolute top-0 left-0 right-0 h-1 z-10"
                style={{ backgroundColor: "var(--brand-primary)" }}
              />

              {/* Google Maps iframe */}
              <iframe
                title="Vị trí Guitar Sài Gòn – 73 ĐHT 31, Tân Hưng Thuận, Quận 12, TPHCM"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3918.0!2d106.627!3d10.874!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z73+%C4%90%C6%B0%E1%BB%9Dng+%C4%90HT+31%2C+Ph%C6%B0%E1%BB%9Dng+T%C3%A2n+H%C6%B0ng+Thu%E1%BA%ADn%2C+Qu%E1%BA%ADn+12%2C+TP.+H%E1%BB%93+Ch%C3%AD+Minh!5e0!3m2!1svi!2svn!4v1"
                className="w-full h-full"
                style={{ border: 0, filter: "saturate(0.9) contrast(1.02)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

              {/* Floating address badge */}
              <div
                className="absolute bottom-5 left-5 right-5 z-10 flex items-center gap-3 px-4 py-3 rounded-2xl border backdrop-blur-sm"
                style={{
                  backgroundColor: "rgba(255,255,255,0.92)",
                  borderColor: "rgba(200,169,110,0.35)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(200,169,110,0.15)" }}
                >
                  <MapPin className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xs font-bold truncate"
                    style={{ fontFamily: "var(--font-montserrat)", color: "#111827" }}
                  >
                    73 ĐHT 31, Tân Hưng Thuận, Q.12
                  </p>
                  <p className="text-xs" style={{ color: "#6b7280" }}>
                    TP. Hồ Chí Minh
                  </p>
                </div>
                <a
                  href="https://maps.google.com/?q=73+ĐHT+31+Tân+Hưng+Thuận+Quận+12+TPHCM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0"
                >
                  <Navigation className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                </a>
              </div>
            </div>

            {/* ── INFO PANEL ── */}
            <div className="p-8 sm:p-10 flex flex-col justify-between">

              <div>
                {/* Section label */}
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-6"
                  style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
                >
                  Thông tin cửa hàng
                </p>

                {/* Info items */}
                <div className="space-y-5 mb-8">
                  {INFO.map(({ icon: Icon, label, value, sub, href }) => (
                    <div key={label} className="flex items-start gap-4">
                      <div
                        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center mt-0.5"
                        style={{
                          backgroundColor: "rgba(200,169,110,0.1)",
                          border: "1.5px solid rgba(200,169,110,0.25)",
                        }}
                      >
                        <Icon className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                      </div>
                      <div className="flex-1">
                        <p
                          className="text-xs font-bold uppercase tracking-wider mb-1"
                          style={{ color: "var(--section-muted)", fontFamily: "var(--font-montserrat)" }}
                        >
                          {label}
                        </p>
                        {href ? (
                          <a
                            href={href}
                            target={href.startsWith("http") ? "_blank" : undefined}
                            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                            className="group inline-flex items-start gap-1 hover:opacity-80 transition-opacity"
                          >
                            <span
                              className="font-semibold text-sm leading-snug"
                              style={{ color: "var(--section-foreground)" }}
                            >
                              {value}
                            </span>
                            <ExternalLink
                              className="w-3 h-3 mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ color: "var(--brand-primary)" }}
                            />
                          </a>
                        ) : (
                          <p
                            className="font-semibold text-sm leading-snug"
                            style={{ color: "var(--section-foreground)" }}
                          >
                            {value}
                          </p>
                        )}
                        {sub && (
                          <p className="text-xs mt-0.5" style={{ color: "var(--section-muted)" }}>
                            {sub}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Nearby areas */}
                <div
                  className="rounded-2xl p-5 border mb-8"
                  style={{
                    backgroundColor: "rgba(200,169,110,0.05)",
                    borderColor: "rgba(200,169,110,0.2)",
                  }}
                >
                  <p
                    className="text-xs font-bold uppercase tracking-wider mb-3"
                    style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
                  >
                    Phục vụ khu vực lân cận
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {NEARBY.map((area) => (
                      <span
                        key={area}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold border"
                        style={{
                          fontFamily: "var(--font-montserrat)",
                          backgroundColor: "var(--section-bg)",
                          borderColor: "var(--section-border)",
                          color: "var(--section-foreground)",
                        }}
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://maps.google.com/?q=73+ĐHT+31+Tân+Hưng+Thuận+Quận+12+TPHCM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex-1 inline-flex items-center justify-center gap-2 font-bold px-5 py-4 rounded-xl transition-all duration-200 hover:opacity-90 active:scale-[0.98] text-sm"
                  style={{
                    backgroundColor: "var(--brand-primary)",
                    color: "var(--brand-primary-foreground)",
                    fontFamily: "var(--font-montserrat)",
                    boxShadow: "0 4px 20px rgba(200,169,110,0.3)",
                  }}
                >
                  <Navigation className="w-4 h-4" />
                  Chỉ Đường Tới Đây
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>

                <a
                  href="tel:0325805893"
                  className="flex-1 inline-flex items-center justify-center gap-2 font-bold px-5 py-4 rounded-xl border transition-all duration-200 active:scale-[0.98] text-sm"
                  style={{
                    borderColor: "rgba(200,169,110,0.4)",
                    color: "var(--brand-primary)",
                    fontFamily: "var(--font-montserrat)",
                    backgroundColor: "rgba(200,169,110,0.06)",
                  }}
                >
                  <Phone className="w-4 h-4" />
                  Gọi Ngay
                </a>
              </div>

              {/* Open now badge */}
              <div
                className="mt-6 flex items-center gap-2 pt-6 border-t"
                style={{ borderColor: "var(--section-border)" }}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: "#22c55e" }}
                />
                <p className="text-sm" style={{ color: "var(--section-muted)" }}>
                  Mở cửa{" "}
                  <span className="font-semibold" style={{ color: "var(--section-foreground)" }}>
                    18:00 – 22:00
                  </span>{" "}
                  mỗi ngày · Kể cả cuối tuần
                </p>
              </div>

            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default LocationSection;